import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ExportRequest {
  id: string;
  user_id: string;
  export_type: string;
  filters: Record<string, any>;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_url?: string;
  file_size?: number;
  progress: number;
  error_message?: string;
  requested_at: string;
  completed_at?: string;
  expires_at: string;
}

export interface CreateExportData {
  export_type: string;
  filters?: Record<string, any>;
}

export function useExportManagement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch export requests
  const exportRequestsQuery = useQuery({
    queryKey: ['export-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('export_requests')
        .select('*')
        .order('requested_at', { ascending: false });
      
      if (error) throw error;
      return data as ExportRequest[];
    },
  });

  // Create export request
  const createExportMutation = useMutation({
    mutationFn: async (exportData: CreateExportData) => {
      const { data, error } = await supabase
        .from('export_requests')
        .insert([{
          ...exportData,
          user_id: (await supabase.auth.getUser()).data.user?.id
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['export-requests'] });
      toast({
        title: "Export request created",
        description: "Your export request has been queued for processing.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create export",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Download export file
  const downloadExport = async (exportRequest: ExportRequest) => {
    if (!exportRequest.file_url) {
      toast({
        title: "File not ready",
        description: "The export file is not ready for download yet.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await fetch(exportRequest.file_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${exportRequest.export_type}_${exportRequest.id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Download started",
        description: "Your export file is being downloaded.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the export file.",
        variant: "destructive",
      });
    }
  };

  // Get export statistics
  const getExportStats = () => {
    const requests = exportRequestsQuery.data || [];
    return {
      total: requests.length,
      completed: requests.filter(r => r.status === 'completed').length,
      failed: requests.filter(r => r.status === 'failed').length,
      pending: requests.filter(r => r.status === 'pending' || r.status === 'processing').length,
      totalSize: requests
        .filter(r => r.file_size)
        .reduce((sum, r) => sum + (r.file_size || 0), 0),
    };
  };

  return {
    exportRequests: exportRequestsQuery.data || [],
    exportStats: getExportStats(),
    isLoading: exportRequestsQuery.isLoading,
    error: exportRequestsQuery.error,
    createExport: createExportMutation.mutate,
    downloadExport,
    isCreating: createExportMutation.isPending,
  };
}